<template>
  <div>
    <!-- Loading -->
    <Loading v-if="loading" class="p-8" />

    <!-- Status -->
    <div v-else class="flex flex-col max-w-2xl min-h-screen mx-auto bg-white shadow">
      <div class="flex items-center justify-between px-4 py-4 text-lg leading-tight bg-white border-b sm:px-8">
        <div>
          <div class="font-bold">
            Order Status
          </div>
          <div class="text-gray-500">
            {{ order.tracking_code }}
          </div>
        </div>
        <div class="flex items-center h-12">
          <img :src="portal.logo" :alt="portal.name" v-if="portal.logo" class="w-auto h-10">
          <div v-else class="max-w-xs font-bold truncate text-accent">
            {{ portal.name }}
          </div>
        </div>
      </div>

      <div>
        <!-- Details -->
        <div class="relative flex items-center flex-none p-4 sm:px-8 sm:py-6">
          <div class="flex items-center justify-center w-16 h-16 text-2xl font-bold text-white rounded-lg bg-accent">
            <fa :icon="['far', 'concierge-bell']" size="lg" fixed-width v-if="order.ordertype === 1" />
            <fa :icon="['far', 'hand-holding-box']" size="lg" fixed-width v-if="order.ordertype === 2" />
            <fa :icon="['far', 'box-alt']" size="lg" fixed-width v-if="order.ordertype === 3" />
          </div>
          <div class="flex-grow ml-4 leading-snug">
            <div class="flex justify-between w-full font-bold text-accent">
              {{ orderType }} Order
            </div>
            <div class="flex items-center" v-if="order.customer_name">
              <span v-if="order.customer_name">
                <span class="text-xs font-medium tracking-wider text-gray-500 uppercase">From: </span>
                <span class="font-medium">{{ order.customer_name }}</span>
              </span>
            </div>
            <div class="flex items-center" v-if="order.contact_name">
              <span v-if="order.contact_name"> 
                <span class="text-xs font-medium tracking-wider text-gray-500 uppercase">Attn: </span>
                <span class="font-medium">{{ order.contact_name }}</span>
                </span>
            </div>
          </div>
        </div>
        
        <!-- Schedule -->
        <div v-if="order.status === 'complete'" class="flex m-4 sm:mx-8 sm:mb-8">
          <div class="w-16 mr-4 text-right text-gray-400 ">
            <fa :icon="['far', 'calendar-check']" size="lg" fixed-width />
          </div>
          <div>
            <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
              Completed At
            </div>
            {{ order.completed_at | moment('dddd, D MMM YYYY [at] HH:mm') }} 
          </div>
        </div>
        <div v-else class="flex m-4 sm:mx-8 sm:mb-8">
          <div class="w-16 mr-4 text-right text-gray-400 ">
            <fa :icon="['far', 'calendar']" size="lg" fixed-width />
          </div>
          <div>
            <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
              <template v-if="order.orderdate">Scheduled for</template>
              <template v-else>Not yet scheduled</template>
            </div>
            {{ order.orderdate | moment('dddd, D MMM YYYY') }} 
          </div>
        </div>

        <!-- Address -->
        <div class="flex m-4 sm:mx-8">
          <div class="w-16 mr-4 text-right text-gray-400 ">
            <fa :icon="['far', 'map-marker-alt']" size="lg" fixed-width />
          </div>
          <div>
            <div class="text-xs font-bold tracking-wider text-gray-500 uppercase">
              <template v-if="order.formatted_address || order.street">Address</template>
              <template v-else>No address specified</template>
            </div>
            <div>
              <div>{{ order.street }}</div>
              <div>{{ order.city }}</div>
              <div>{{ order.zip }}</div>
              <div>{{ order.country_name }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="p-4">
        <!-- Update Address -->
        <template v-if="portal.tracking_link_settings.address_updates_enabled && order.orderstate < 200">
          <router-link v-if="this.order.latitude && this.order.longitude" 
            :to="{ name: 'TrackingLinkUpdateAddress', params: { trackingCode: trackingCode } }" 
            class="flex justify-between p-3 mt-4 leading-tight rounded bg-accent sm:p-5 hover:no-underline bg-opacity-10 text-accent">
            <div>
              <div class="font-bold">Incorrect Address?</div>
              <div>Update it here.</div>
            </div>
            <div class="bg-opacity-100 btn btn-accent">
              Change Address
            </div>
          </router-link>
        </template>

        <!-- Feedback -->
        <template v-if="portal.tracking_link_settings.feedback_enabled && order.orderstate > 400">
          <!-- Send Feedback -->
          <router-link v-if="order.feedback_stars === null" :to="{ name: 'TrackingLinkSendFeedback', params: { trackingCode: trackingCode } }" class="flex justify-between p-3 mt-4 leading-tight rounded bg-accent sm:p-5 hover:no-underline bg-opacity-10 text-accent">
            <div>
              <div class="font-bold">Order Complete</div>
              <div>Rate your experience.</div>
            </div>
            <div class="bg-opacity-100 btn btn-accent">
              Give Feedback
            </div>
          </router-link>
          <!-- Thanks for Feedback -->
          <div v-else class="flex justify-between p-3 mt-4 leading-tight rounded bg-accent sm:p-5 hover:no-underline bg-opacity-10 text-accent">
            <div>
              <div class="font-bold">Thank you!</div>
              <div>Your feedback has been received.</div>
            </div>
          </div>
        </template>
      </div>

      <!-- Map -->
      <div class="flex-grow w-full h-56 bg-gray-400" v-if="this.order.latitude && this.order.longitude">
        <mgl-map class="w-full h-full border-t"
          v-if="orderCoordinates[0] !== 0 && orderCoordinates[1] !== 0"
          ref="map"
          :center="mapCenter" 
          :access-token="accessToken"
          :map-style="mapStyle"
          :zoom="zoom"
          :interactive="false">
          <mgl-marker :coordinates.sync="orderCoordinates" anchor="bottom">
            <div slot="marker">
              <svg class="w-6 text-accent" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="currentColor" d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"></path><circle cx="192" cy="192" fill="#fff" r="80" /></svg>
            </div>
          </mgl-marker>
        </mgl-map>
      </div>

    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import { MglMap, MglMarker } from 'vue-mapbox'

  
  export default {
    name: 'Status',

    metaInfo: {
      title: 'Order Status'
    },

    components: {
      MglMap,
      MglMarker
    },

    created () {
      this.fetchStatus()
    },

    computed: {
      ...mapState(['portal']),
      
      trackingCode () {
        return this.$route.params.trackingCode
      },

      orderCoordinates () {
        if (this.order) {
          return [this.order.longitude, this.order.latitude]
        } else {
          return [0, 0]
        }
      },

      mapCenter () {
        if (this.order) {
          return [this.order.longitude, parseFloat(this.order.latitude) + 0.00025]
        } else {
          return [0, 0]
        }
      },
      
      orderType () {
        const typeEnum = {
          '1': 'Service',
          '2': 'Pickup',
          '3': 'Delivery'
        }

        return typeEnum[this.order.ordertype]
      }
    },

    data () {
      return {
        loading: true,
        order: {},
        accessToken: 'pk.eyJ1IjoiZmxlZXRiZWFjb24iLCJhIjoiY2puZXczdHhmMHFqeDNrbGZ3dGNmbGU2cCJ9.dZ0JMOD5222pAk-pR6wzYQ',
        mapStyle: 'mapbox://styles/fleetbeacon/ckcw7i47m15m71imu9z9329lr',
        zoom: 16
      }
    },

    methods: {
      async fetchStatus () {
        try {
          const { data } = await this.$api.get(`tracking-links/${this.trackingCode}/status`)

          /** 
           * Redirect to the live tracking view if order is in progress 
           */
          const liveTrackingEnabled = data.order.account.tracking_link_settings.live_tracking_enabled
          const orderInProgress = data.order.orderstate > 200 && data.order.orderstate < 299
          if (liveTrackingEnabled && orderInProgress) {
            this.$router.push({
              name: 'TrackingLinkTrackDriver', 
              params: { 
                trackingCode: this.trackingCode 
              } 
            })
          }

          this.order = data.order
          this.loading = false
        } catch (error) {
          console.error(error)
          this.$toasted.error(error.response.data.message)
        }
      }
    }
  }
</script>